import type { Position } from 'components/map/types';
import type { HubFE } from 'domain/hub';
import type { VehicleFE } from 'domain/vehicle';
import { toRioPosition, transformToRioPosition } from 'helper/position';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBoundingBox, setBoundingBox } from 'store/slices/appSlice';

import { calculateBoundingBox, getMapPosition } from '../utils';

import type { Position as ApiPosition } from 'API';

/**
 * Two `Position` types on purpose. `focusOnMissionRoute` now takes UIKit's `{ lat, lng }`, which
 * `GeoPositionFE` is structurally assignable to, so `Map.tsx` passes `route.positions` straight
 * through. `hubs` is domain-shaped too, since hubs now arrive over REST as `HubFE`. The hook's own
 * `positions` parameter keeps the GraphQL `{ latitude, longitude }` shape; `Map.tsx` is its only
 * caller and passes nothing, so converting it is left out of this change.
 */
export const useBoundingBox = (vehicles: VehicleFE[], hubs?: HubFE[], positions?: ApiPosition[]) => {
    const dispatch = useDispatch();
    const boundingBox = useSelector(getBoundingBox);

    const updateBoundingBox = useCallback(() => {
        const vehiclePositions = vehicles
            .filter(vehicle => !!vehicle.position.value?.value)
            .map(vehicle => getMapPosition(vehicle));

        const hubPositions = hubs ? hubs.map(hub => toRioPosition(hub.position)) : [];
        const otherPositions = positions ? positions.map(transformToRioPosition) : [];

        dispatch(setBoundingBox(calculateBoundingBox([...vehiclePositions, ...hubPositions, ...otherPositions])));
    }, [vehicles, hubs, positions, dispatch]);

    const focusOnMissionRoute = (missionVehicle: VehicleFE, missionWaypoints: Position[]) => {
        const vehiclePosition = getMapPosition(missionVehicle);

        dispatch(setBoundingBox(calculateBoundingBox([vehiclePosition, ...missionWaypoints])));
    };

    const resetBoundingBox = () => {
        dispatch(setBoundingBox(undefined));
    };

    useEffect(() => {
        window.addEventListener('resize', updateBoundingBox);
        return () => {
            window.removeEventListener('resize', updateBoundingBox);
        };
    }, [updateBoundingBox]);

    useEffect(() => {
        updateBoundingBox();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicles.length, hubs?.length, positions?.length]);

    return { boundingBox, updateBoundingBox, focusOnMissionRoute, resetBoundingBox };
};
